
import React from 'react';

interface ChecklistItem {
  label: string;
  status: 'pass' | 'warning' | 'fail';
  detail: string;
}

interface EligibilityChecklistProps {
  items: ChecklistItem[];
}

/**
 * Renders a detailed eligibility checklist with summary pills.
 * Includes defensive checks and fallback handling for unexpected status values.
 */
export function EligibilityChecklist({ items }: EligibilityChecklistProps) {
  // Configuration for each possible status
  const statusConfig: Record<
    ChecklistItem['status'],
    {
      icon: string;
      color: string;
      bg: string;
      border: string;
      label: string;
    }
  > = {
    pass: {
      icon: 'ri-checkbox-circle-fill',
      color: 'text-emerald-500',
      bg: 'bg-emerald-50',
      border: 'border-emerald-100',
      label: 'Meets Requirement',
    },
    warning: {
      icon: 'ri-error-warning-fill',
      color: 'text-amber-500',
      bg: 'bg-amber-50',
      border: 'border-amber-100',
      label: 'Needs Attention',
    },
    fail: {
      icon: 'ri-close-circle-fill',
      color: 'text-red-500',
      bg: 'bg-red-50',
      border: 'border-red-100',
      label: 'Does Not Meet',
    },
  };

  // Defensive counting – ensures `items` is an array
  const safeItems = Array.isArray(items) ? items : [];

  const passCount = safeItems.filter((i) => i.status === 'pass').length;
  const warnCount = safeItems.filter((i) => i.status === 'warning').length;
  const failCount = safeItems.filter((i) => i.status === 'fail').length;

  return (
    <section className="py-12">
      <div className="max-w-3xl mx-auto px-6">
        <h2 className="font-['Fraunces'] text-3xl font-bold text-[#505A5A] mb-2">
          Detailed Breakdown
        </h2>
        <p className="text-[#505A5A]/60 mb-6 text-sm">
          Each factor below is evaluated based on your city’s current short-term rental regulations.
        </p>

        {/* Summary pills */}
        <div className="flex flex-wrap gap-3 mb-8">
          <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-100 rounded-full px-4 py-1.5">
            <i className="ri-checkbox-circle-fill text-emerald-500"></i>
            <span className="text-sm font-medium text-emerald-700">{passCount} Passed</span>
          </div>
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-100 rounded-full px-4 py-1.5">
            <i className="ri-error-warning-fill text-amber-500"></i>
            <span className="text-sm font-medium text-amber-700">{warnCount} Needs Attention</span>
          </div>
          {failCount > 0 && (
            <div className="flex items-center gap-2 bg-red-50 border border-red-100 rounded-full px-4 py-1.5">
              <i className="ri-close-circle-fill text-red-500"></i>
              <span className="text-sm font-medium text-red-700">{failCount} Not Met</span>
            </div>
          )}
        </div>

        {/* Checklist */}
        <div className="space-y-4">
          {safeItems.map((item, index) => {
            // Guard against an unexpected status value
            const cfg = statusConfig[item.status] ?? {
              icon: 'ri-question-mark',
              color: 'text-gray-500',
              bg: 'bg-gray-50',
              border: 'border-gray-100',
              label: 'Unknown',
            };

            return (
              <div
                key={index}
                className={`${cfg.bg} border ${cfg.border} rounded-xl p-5 transition-all hover:shadow-md`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className={`${cfg.icon} ${cfg.color} text-xl`}></i>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="text-base font-semibold text-[#505A5A]">{item.label}</h3>
                      <span className={`text-xs font-medium ${cfg.color} whitespace-nowrap`}>
                        {cfg.label}
                      </span>
                    </div>
                    <p className="text-sm text-[#505A5A]/70 leading-relaxed">{item.detail}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
